import React, { useState, useEffect, useRef } from 'react';

interface PopupProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  mode?: 'info' | 'confirmation';
  onConfirm?: () => void;
  confirmText?: string;
  cancelText?: string;
  closeText?: string;
}

const Popup: React.FC<PopupProps> = ({
  isOpen,
  onClose,
  title,
  children,
  mode = 'info',
  onConfirm,
  confirmText = 'I understand and accept',
  cancelText = 'Back',
  closeText = 'Close'
}) => {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  // Reset scroll state when popup opens
  useEffect(() => {
    if (isOpen) {
      setHasScrolledToBottom(false);
    }
  }, [isOpen]);

  // Handle scroll detection for confirmation mode
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (mode !== 'confirmation') return;
    
    const element = e.currentTarget;
    const threshold = 10; // Allow small margin for scroll detection
    const isScrolledToBottom = 
      element.scrollHeight - element.scrollTop - element.clientHeight <= threshold;
    
    setHasScrolledToBottom(isScrolledToBottom);
  };

  // Check if content is scrollable
  useEffect(() => {
    if (mode === 'confirmation' && contentRef.current) {
      const element = contentRef.current;
      const isContentScrollable = element.scrollHeight > element.clientHeight;
      
      // If content is not scrollable, allow confirmation immediately
      if (!isContentScrollable) {
        setHasScrolledToBottom(true);
      }
    }
  }, [mode, children]);

  const handleConfirm = () => {
    if (mode === 'confirmation' && !hasScrolledToBottom) {
      return; // Don't allow confirmation if not scrolled to bottom
    }
    onConfirm?.();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 sm:p-8"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-lg shadow-xl w-full sm:w-[80%] max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 text-center flex-1">
            {title}
          </h2>
          {mode === 'info' && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-1 ml-4"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          )}
        </div>

        {/* Content */}
        <div 
          ref={contentRef}
          className="flex-1 overflow-y-auto p-6 text-sm text-gray-700 leading-relaxed"
          onScroll={handleScroll}
        >
          {children}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200">
          {mode === 'info' ? (
            <button
              onClick={onClose}
              className="w-full bg-gray-800 text-white py-3 px-4 rounded-lg hover:bg-gray-700 transition-colors font-medium"
            >
              {closeText}
            </button>
          ) : (
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 bg-gray-800 text-white py-3 px-4 rounded-lg hover:bg-gray-700 transition-colors font-medium"
              >
                {cancelText}
              </button>
              <button
                onClick={handleConfirm}
                disabled={!hasScrolledToBottom}
                className={`flex-1 py-3 px-4 rounded-lg font-medium transition-colors ${
                  hasScrolledToBottom
                    ? 'bg-gray-300 text-gray-800 hover:bg-gray-200 cursor-pointer'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                {confirmText}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Popup;